import React from 'react';
import {translations} from 'presentation/translations-new';
import {GuideFilterEnum} from 'main/analytics/gaTagsEnum';
import {textHandle} from 'presentation/utils/textHandle';
import {FeaturesEnum, HealthGuideServicesTypeEnum, QueryParamsEnum} from 'presentation/constants';
import {useQueryParams} from 'presentation/pages/private';
import {AutocompleteSelect} from '../SearchDetails/AutocompleteSelect';
import {SkeletonInput} from '../SkeletonInput';
import {HealthSpecialtiesInputProps} from './types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {SpecialtyMedicalGuideModel} from 'domain/usecases';

const {selectSpecialty} = translations['pt-br'].privateFlow.guideFlow;

export function HealthSpecialtiesInput({
	healthSpecialties,
	isLoading,
}: HealthSpecialtiesInputProps) {
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {serviceType, specialtyName} = SEARCH_PARAMS;
	const {isEnabled: isSpecialtyDisabledEmergencyFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_DISABLE_SPECIALTY_INPUT_FOR_EMERGENCY,
	);
	const {isEnabled: isSpecialtyDisabledElectiveFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_DISABLE_SPECIALTY_INPUT_FOR_ELECTIVE_HOSPITAL,
	);

	const healthSpecialtiesFiltered = healthSpecialties?.filter(
		specialty => specialty?.nmEspecialidade,
	);

	if (isLoading) {
		return (
			<SkeletonInput label={translations['pt-br'].privateFlow.guideFlow.loadingSpecialties} />
		);
	}

	const shouldDisableInput =
		healthSpecialtiesFiltered?.length === 0 ||
		!serviceType ||
		serviceType === HealthGuideServicesTypeEnum.MATERNITY ||
		(isSpecialtyDisabledEmergencyFlagEnabled &&
			serviceType === HealthGuideServicesTypeEnum.EMERGENCY) ||
		(isSpecialtyDisabledElectiveFlagEnabled &&
			serviceType === HealthGuideServicesTypeEnum.ELECTIVE_HOSPITAL);

	const handleGetOptionValue = (opt: SpecialtyMedicalGuideModel) => {
		return opt?.nmEspecialidade ?? '';
	};

	const getSpecialtyByName = (name: string) => {
		return (
			healthSpecialtiesFiltered?.find(specialty => specialty.nmEspecialidade === name) || null
		);
	};

	const handleOnValueChange = (val: string) => {
		setSearchParams(prev => {
			const specialtyCode = getSpecialtyByName(val)?.cdEspecialidade ?? '';
			const hasSubSpecialty = String(getSpecialtyByName(val)?.flSubespecialidade === true);
			prev.set(QueryParamsEnum.SPECIALTY_NAME, val);
			prev.set(QueryParamsEnum.SPECIALTY_CODE, specialtyCode);
			prev.set(QueryParamsEnum.HAS_SUB_SPECIALTY, hasSubSpecialty);
			return prev;
		});
	};

	const handleGetOptionLabel = (opt: SpecialtyMedicalGuideModel) =>
		textHandle(opt?.nmEspecialidade).capitalizeFirstLetter() || '';

	return (
		<AutocompleteSelect
			dataTagId={GuideFilterEnum.APLICOU_FILTRO_ESPECIALIDADE_SAUDE}
			key={serviceType}
			disabled={shouldDisableInput}
			customPlaceholder={selectSpecialty}
			value={specialtyName || ''}
			onValueChange={handleOnValueChange}
			options={healthSpecialtiesFiltered ?? []}
			getOptionLabel={handleGetOptionLabel}
			getOptionValue={handleGetOptionValue}
		/>
	);
}
